import React, { useEffect } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ReactComponent as Organizations } from "../../../../images/Organizations.svg";
import { ReactComponent as POS } from "../../../../images/POS.svg";
import { ReactComponent as Tick } from "../../../../images/Ticketing.svg";
import { ReactComponent as AccreditationsIcon } from "../../../../images/Accreditations.svg";
import { ReactComponent as Takeaway } from "../../../../images/Webshop.svg";
import { ReactComponent as Marketplace } from "../../../../images/Marketplace.svg";
import { ReactComponent as TablePlan } from "../../../../images/Tables.svg";
import { ReactComponent as Cashless } from "../../../../images/Cashless.svg";
import { ReactComponent as BarTrack } from "../../../../images/BarTrack.svg";
import { ReactComponent as Stock } from "../../../../images/Stock.svg";
import { ReactComponent as Budget } from "../../../../images/Cash.svg";
import { ReactComponent as NotesIcon } from "../../../../images/Meeting.svg";
import LanguageSwitcher from "../../../../components/LanguageSwitcher.jsx";
import { useSelector } from "react-redux";
import { Collapse } from "react-bootstrap";
import logo from "../../../../components/logos/logo.svg";
import closeSidebar from "../../../../images/closeSidebar.png";
import ENV from "../../../../env";
import {
  DASHBOARD_APP_IDS,
  getSelectedDashboardAppIds,
} from "../../../../components/dashboardAppSelector/dashboardApps";

function Sidebar({
  organizations,
  setOrgId,
  orgId,
  sidebarOpen,
  toggleSidebar,
  isCreatingNewPos = false,
  onOpenAppSelector,
}) {
  const navigate = useNavigate();
  const { next } = useParams();
  const { t } = useTranslation("sidebar");
  const userStore = useSelector((state) => state.userReducer);
  const location = useLocation();
  const visibleAppIds = getSelectedDashboardAppIds(userStore?.users?.dashboardApps);
  const visibleAppIdSet = new Set(visibleAppIds);
  const isAppVisible = (appId) => visibleAppIdSet.has(appId);
  const isPosActive = ["pos", "overview", "history"].includes(next);

  useEffect(() => {
    if (isCreatingNewPos && location.pathname.startsWith("/dashboard/organizations")) {
      return;
    }
    if (orgId.length === 0) {
      handlePosOrg(userStore?.users.activeOrg);
    } else if (orgId.length > 1 && !location.pathname.startsWith("/dashboard/overview")) {
      setOrgId([orgId[0]]);
    }
  }, [orgId, location.pathname, isCreatingNewPos]);

  function handleOrg(id) {
    if (orgId.includes(id) && orgId.length > 1) {
      setOrgId(orgId.filter((org) => org !== id));
    } else {
      setOrgId([...orgId, id]);
    }
  }

  const handlePosOrg = (id) => {
    setOrgId([id]);
  };
  const arrowClass = (isOpen) => `arrow ${isOpen ? "right" : "up"}`;

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 768 && sidebarOpen) {
      toggleSidebar();
    }
  };

  const renderActiveUnderline = (isOpen) => (
    <Collapse in={isOpen}>
      <div>
        <div className="py-2 collapse-inner sidebar-active-underline"></div>
      </div>
    </Collapse>
  );

  const handleRestrictedMenuClick = (e) => {
    if (!orgId[0]) {
      e.preventDefault();
      navigate("/dashboard/organizations");
      closeSidebarOnMobile();
      return;
    }
    closeSidebarOnMobile();
  };

  return (
    <>
      <ul className="navbar-nav sidebar sidebar-dark accordion zi-1" id="accordionSidebar">
        <li className="nav-item">
          <div className="sidebar-brand d-flex align-items-center justify-content-center mt-2">
            <div className="sidebar-brand-text ">
              <div className="sidebar-brand-logo-slot">
                <img src={logo} alt="siepay" />
              </div>
              <button
                onClick={toggleSidebar}
                aria-label="Close sidebar"
                className="sidebarToggleButton"
              >
                <img src={closeSidebar} alt="Close sidebar" />
              </button>
            </div>
          </div>
        </li>
        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.POS) && (
          <>
            <li className="nav-item">
              <NavLink
                className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
                to="/dashboard/pos"
                onClick={handleRestrictedMenuClick}
              >
                <POS />
                <span>{t("pos_app")}</span>
                <i className={arrowClass(isPosActive)}></i>
              </NavLink>
              <Collapse in={isPosActive}>
                <div>
                  <div className="py-2 collapse-inner">
                    <NavLink
                      className={`collapse-item ${next === "pos" ? "active" : ""}`}
                      to="/dashboard/pos"
                      onClick={handleRestrictedMenuClick}
                    >
                      {t("configure_pos")}
                    </NavLink>
                    <NavLink
                      className={`collapse-item ${next === "history" ? "active" : ""}`}
                      to="/dashboard/history"
                      onClick={handleRestrictedMenuClick}
                    >
                      {t("history")}
                    </NavLink>
                    <NavLink
                      className={`collapse-item ${next === "overview" ? "active" : ""}`}
                      to="/dashboard/overview"
                      onClick={handleRestrictedMenuClick}
                    >
                      {t("revenues")}
                    </NavLink>
                    {next === "overview" && (
                      <>
                        <div className="sidebar-pos-org-actions">
                          <button
                            type="button"
                            className="sidebar-pos-org-action"
                            onClick={() => {
                              setOrgId(organizations.map((o) => o.id));
                            }}
                          >
                            {t("select_all")}
                          </button>
                          <button
                            type="button"
                            className="sidebar-pos-org-action"
                            onClick={() => {
                              if (organizations.length > 0) {
                                setOrgId([organizations[0].id]);
                              }
                            }}
                          >
                            {t("only_first")}
                          </button>
                        </div>
                        {organizations.map((val, key) => (
                          <a
                            key={key}
                            className={
                              orgId.includes(val?.id)
                                ? "collapse-item-box d-flex ai-center active"
                                : "collapse-item-box d-flex ai-center"
                            }
                            href="#"
                            onClick={() => handleOrg(val?.id)}
                          >
                            <div className="activeBox">
                              <span></span>
                            </div>
                            <span className="item-label">{val?.organization}</span>
                          </a>
                        ))}
                        {organizations.length === 0 && (
                          <a className="collapse-item">{t("no_cash_registers")}</a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Collapse>
              {renderActiveUnderline(isPosActive)}
            </li>
          </>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.TABLE_PLAN) && (
          <>
            <li className="nav-item">
              <NavLink
                className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
                to="/dashboard/table-plan"
                onClick={handleRestrictedMenuClick}
              >
                <TablePlan />
                <span>{t("table_plan")}</span>
                <i className={arrowClass(next === "table-plan")}></i>
              </NavLink>
              {renderActiveUnderline(next === "table-plan")}
            </li>
          </>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.CASHLESS) && (
          <>
            <li className="nav-item">
              <NavLink
                className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
                to="/dashboard/cashless-cards"
                onClick={handleRestrictedMenuClick}
              >
                <Cashless />
                <span>{t("cashless")}</span>
                <i className={arrowClass(next === "cashless-cards")}></i>
              </NavLink>
              {renderActiveUnderline(next === "cashless-cards")}
            </li>
          </>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.TICKETING) && (
          <>
            <li className="nav-item">
              <NavLink
                to={"/dashboard/ticketing"}
                className={() =>
                  `nav-link ${location.pathname.startsWith("/dashboard/ticketing") ? "active" : ""} ${!orgId[0] ? "disabled-link" : ""}`
                }
                onClick={handleRestrictedMenuClick}
              >
                <Tick />
                <span>Ticketing</span>
                <i className={arrowClass(location.pathname.includes("ticketing"))}></i>
              </NavLink>
              {renderActiveUnderline(location.pathname.includes("ticketing"))}
            </li>

          </>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.STOCK) && (
          <li className="nav-item">
            <NavLink
              className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
              to="/dashboard/stock-management"
              onClick={handleRestrictedMenuClick}
            >
              <Stock />
              <span>{t("stock")}</span>
              <i className={arrowClass(next === "stock-management")}></i>
            </NavLink>
            {renderActiveUnderline(next === "stock-management")}
          </li>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.ACCREDITATIONS) && (
          <li className="nav-item">
            <NavLink
              className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
              to="/dashboard/accreditations"
              onClick={handleRestrictedMenuClick}
            >
              <AccreditationsIcon />
              <span>{t("accreditations")}</span>
              <i className={arrowClass(next === "accreditations")}></i>
            </NavLink>
            {renderActiveUnderline(next === "accreditations")}
          </li>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.BUDGET) && (
          <li className="nav-item budget-nav-item">
            <NavLink
              className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
              to="/dashboard/budget"
              onClick={handleRestrictedMenuClick}
              >
                <Budget />
                <span>{t("budget")}</span>
                <i className={arrowClass(next === "budget")}></i>
              </NavLink>
            {renderActiveUnderline(next === "budget")}
          </li>
        )}

        {organizations.length > 0 && isAppVisible(DASHBOARD_APP_IDS.NOTES) && (
          <li className="nav-item">
            <NavLink
              className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
              to="/dashboard/notes"
              onClick={handleRestrictedMenuClick}
            >
              <NotesIcon />
              <span>{t("notes")}</span>
              <i className={arrowClass(next === "notes")}></i>
            </NavLink>
            {renderActiveUnderline(next === "notes")}
          </li>
        )}

        {organizations.length > 0 && (
          <>
            {isAppVisible(DASHBOARD_APP_IDS.TAKEAWAY) && <li className="nav-item">
              <NavLink
                className={() =>
                  `nav-link ${location.pathname.startsWith("/dashboard/takeaway") ? "active" : ""} ${!orgId[0] ? "disabled-link" : ""}`
                }
                to={"/dashboard/takeaway-overview"}
                onClick={handleRestrictedMenuClick}
              >
                <Takeaway />
                <span>Takeaway</span>
                <i className={arrowClass(location.pathname.includes("takeaway"))}></i>
              </NavLink>
              {renderActiveUnderline(location.pathname.includes("takeaway"))}
            </li>}

            {isAppVisible(DASHBOARD_APP_IDS.BARTRACK) && <li className="nav-item">
              <NavLink
                className={`nav-link ${!orgId[0] ? "disabled-link" : ""}`}
                to="/dashboard/bartrack-overview"
                onClick={handleRestrictedMenuClick}
              >
                <BarTrack />
                <span>Bartrack</span>
                <i className={arrowClass(location.pathname.includes("bartrack"))}></i>
              </NavLink>
              {renderActiveUnderline(location.pathname.includes("bartrack"))}
            </li>}
          </>
        )}

        <li className="nav-item">
          <NavLink className="nav-link" to="/dashboard/organizations" onClick={closeSidebarOnMobile}>
            <Organizations />
            <span>{t("organization")}</span>
            <i className={arrowClass(next === "organizations")}></i>
          </NavLink>
          {renderActiveUnderline(next === "organizations")}
        </li>
        {ENV.ENABLE_MARKETPLACE && (
          <li className="nav-item mb-0 mt-auto">
            <NavLink className="nav-link" to="/dashboard/marketplace" onClick={closeSidebarOnMobile}>
              <Marketplace />
              <span>Marketplace</span>
            </NavLink>
          </li>
        )}

        <li className={`nav-item mb-0 ${ENV.ENABLE_MARKETPLACE ? "" : "mt-auto"}`}>
          <button
            type="button"
            className="nav-link sidebar-add-app-button"
            onClick={() => {
              closeSidebarOnMobile();
              onOpenAppSelector?.();
            }}
          >
            <span className="sidebar-add-app-button-icon" aria-hidden="true"></span>
            <span>{t("add_application")}</span>
          </button>
        </li>

        <li className="nav-item mb-0">
          <div className="fw-500 w-100 py-3 version">
            <LanguageSwitcher />
          </div>
        </li>
      </ul>
      {/* End of Sidebar */}
    </>
  );
}
export default Sidebar;
